import type React from "react";
import { useEffect, useRef, useState, type PropsWithChildren, type ReactNode } from "react";

import { SearchRankingProvider } from "../SearchRanking";

import { SearchOverlayContext } from "./context";

interface Props {
  closeSectionLabel?: ReactNode;
  enabled: boolean;
}

const FOCUSABLE_SELECTOR =
  'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const SearchOverlay: React.FC<PropsWithChildren<Props>> = ({
  children,
  enabled,
  closeSectionLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const showOverlay = enabled && isOpen;
  const scrollTo = useRef(0);

  /**
   * we're creating a full-screen modal. Thats mostly handled in the CSS. but we
   * don't want it to mess with the the underlying scroll. So we track the
   * existing scroll location, turn off scrolling & move to the top. Then we
   * undo that when we close.
   */
  const setShowOverlay = (state: boolean) => {
    if (!enabled) return;
    const inFrame = window !== window.top;

    if (state && isOpen === false) {
      scrollTo.current = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.backgroundColor = "rgba(0,0,0,.1)";
      window.scrollTo({ top: 0 });

      // We're in a frame, hard set the iframe body element to the modal max
      // height or the current document height. Whichever is smaller.
      // When turning off the overlay we unset this.
      if (inFrame) {
        // calc(100vh - 80px) is the modal css max height
        // It would be nice to be able to do this entirely in css
        // but css lengths fall down a bit inside frames.
        const modalMax = window.screen.availHeight - 80;
        const currentHeight = document.body.clientHeight;

        document.body.style.height = `${Math.min(currentHeight, modalMax)}px`;
      }

      if (containerRef.current) {
        setTimeout(() => {
          containerRef.current.scrollIntoView();
        }, 0);
      }
    }

    if (!state) {
      document.body.style.overflow = "unset";
      document.body.style.backgroundColor = "unset";
      window.scrollTo({ top: scrollTo.current });
      if (inFrame) {
        document.body.style.height = "unset";
      }
    }

    setIsOpen(state);
  };

  // Focus trapping: keep Tab/Shift+Tab within overlay, close on Escape
  useEffect(() => {
    if (!showOverlay) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const container = containerRef.current;
      if (!container) return;

      const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showOverlay]);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <SearchOverlayContext.Provider value={{ showOverlay, setShowOverlay }}>
      <SearchRankingProvider>
        <div className={showOverlay && "sid-search-overlay__overlay"}>
          <div className={showOverlay && "sid-search-overlay__container"} ref={containerRef}>
            {showOverlay && (
              <button className="sid-search-overlay__close" onClick={() => setShowOverlay(false)}>
                {closeSectionLabel}
              </button>
            )}
            {children}
          </div>
        </div>
      </SearchRankingProvider>
    </SearchOverlayContext.Provider>
  );
};
